import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import SidebarLayout from "../components/sidebar/SidebarLayout";
import ContentWrapper from "../components/layout/ContentWrapper";
import PageLoadSpinner from "../components/layout/PageLoadSpinner";
import PageTitle from "../components/layout/PageTitle";
import Button from "../components/layout/Button";
import Select from "../components/layout/Select";
import ConfirmModal from "../components/ivrs/ConfirmModal";
//@ts-ignore
import Card from "../components/layout/Card";
//@ts-ignore
import TextArea from "../components/layout/TextArea";
//@ts-ignore
import IntegerInput from "../components/layout/IntegerInput";
import Switch from "../components/layout/Switch";
import MenuConfig from "../components/whatsappBot/MenuConfig";
import useWhatsappBotPageHook from "../hooks/WhatsappBotPageHook";

function WhatsappBotPage() {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [navigateTo, setNavigateTo] = useState("");

  const history = useHistory();
  const bot = useWhatsappBotPageHook();

  const onPageChange = (destination: string) => {
    if (bot.hasUnsavedChanges) {
      setShowConfirmModal(true);
      setNavigateTo(destination);
      return false;
    }
    return true;
  };

  const onConfirm = () => {
    history.push(navigateTo);
    setNavigateTo("");
  };

  useEffect(() => {
    bot.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SidebarLayout onPageChange={onPageChange}>
      <PageLoadSpinner show={bot.isLoading} />
      <ContentWrapper show={!bot.isLoading}>
        <PageTitle>Bot de Atendimento (WhatsApp)</PageTitle>
        <Card className="mb-4 w-full flex flex-wrap items-center gap-8">
          <div className="flex items-center gap-2">
            <Switch
              value={bot.enabled}
              onChange={(v) => {
                bot.setEnabled(v);
                bot.setHasUnsavedChanges(true);
              }}
            />
            <span className="font-bold text-primary text-xs 2xl:text-sm">Bot ativado</span>
          </div>
          <Select
            title="Fila padrão (quando o contato não escolhe nenhuma opção)"
            className="min-w-[280px]"
            value={bot.fallbackQueueId}
            setter={(v: string) => {
              bot.setFallbackQueueId(v);
              bot.setHasUnsavedChanges(true);
            }}
            //@ts-ignore
            formValidation={bot.formValidation}
            required={bot.enabled}
          >
            {bot.queues.map((q, i) => (
              <option value={q.id} key={i}>
                {q.name}
              </option>
            ))}
          </Select>
        </Card>

        <Card className="mb-4 w-full">
          <div className="flex items-center gap-2 mb-2">
            <Switch
              value={bot.afterHoursEnabled}
              onChange={(v) => {
                bot.setAfterHoursEnabled(v);
                bot.setHasUnsavedChanges(true);
              }}
            />
            <span className="font-bold text-primary text-xs 2xl:text-sm">
              Avisar fora do horário de atendimento
            </span>
          </div>
          <p className="text-xs text-gray-dark mb-4">
            Usa o mesmo horário já configurado em{" "}
            <span className="font-bold">Horários de Atendimento</span>{" "}
            (incluindo feriados/exceções cadastrados lá) — não precisa
            configurar de novo aqui. Vale pra qualquer mensagem que abra uma
            conversa nova, mesmo com o bot de menu acima desligado.
          </p>
          {bot.afterHoursEnabled && (
            <div className="flex flex-col gap-4">
              <TextArea
                title="Mensagem automática"
                className="w-full"
                value={bot.afterHoursMessage}
                setter={(v: string) => {
                  bot.setAfterHoursMessage(v);
                  bot.setHasUnsavedChanges(true);
                }}
                //@ts-ignore
                formValidation={bot.formValidation}
                required={bot.afterHoursEnabled}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Switch
                    value={bot.afterHoursRouteToQueue}
                    onChange={(v) => {
                      bot.setAfterHoursRouteToQueue(v);
                      bot.setHasUnsavedChanges(true);
                    }}
                  />
                  <span className="font-bold text-primary text-xs 2xl:text-sm">
                    Encaminhar mesmo assim para fila (agente atende no próximo
                    expediente)
                  </span>
                </div>
                <p className="text-xs text-gray-dark mt-1">
                  Usa a mesma "Fila padrão" configurada ali em cima, ao lado
                  de "Bot ativado".
                </p>
              </div>
            </div>
          )}
        </Card>

        <Card className="mb-4 w-full">
          <div className="flex items-center gap-2 mb-2">
            <Switch
              value={bot.expirationEnabled}
              onChange={(v) => {
                bot.setExpirationEnabled(v);
                bot.setHasUnsavedChanges(true);
              }}
            />
            <span className="font-bold text-primary text-xs 2xl:text-sm">
              Expirar conversa parada no bot
            </span>
          </div>
          <p className="text-xs text-gray-dark mb-4">
            Só afeta conversa que o contato abriu com o bot e nunca chegou a
            escolher uma opção válida. Conversa já em fila ou em atendimento
            nunca expira por aqui.
          </p>
          {bot.expirationEnabled && (
            <div className="flex flex-col gap-4">
              <IntegerInput
                title="Expirar após quantas horas sem resposta"
                className="max-w-[220px]"
                value={bot.expirationHours}
                setter={(v: string) => {
                  bot.setExpirationHours(Number(v) || 0);
                  bot.setHasUnsavedChanges(true);
                }}
                //@ts-ignore
                formValidation={bot.formValidation}
                required={bot.expirationEnabled}
              />
              <TextArea
                title="Mensagem de encerramento"
                className="w-full"
                value={bot.expirationMessage}
                setter={(v: string) => {
                  bot.setExpirationMessage(v);
                  bot.setHasUnsavedChanges(true);
                }}
                //@ts-ignore
                formValidation={bot.formValidation}
                required={bot.expirationEnabled}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Switch
                    value={bot.expirationRouteToQueue}
                    onChange={(v) => {
                      bot.setExpirationRouteToQueue(v);
                      bot.setHasUnsavedChanges(true);
                    }}
                  />
                  <span className="font-bold text-primary text-xs 2xl:text-sm">
                    Encaminhar para fila em vez de encerrar
                  </span>
                </div>
                <p className="text-xs text-gray-dark mt-1">
                  Desligado: encerra o atendimento. Ligado: usa a mesma "Fila
                  padrão" configurada ali em cima, ao lado de "Bot ativado" —
                  agente atende quando puder, sem o contato precisar
                  escrever de novo.
                </p>
              </div>
            </div>
          )}
        </Card>

        <MenuConfig botHook={bot} />

        <div className="mt-4 flex justify-end items-center">
          <div className="relative">
            {bot.hasUnsavedChanges && (
              <>
                <div className="rounded-full w-4 h-4 bg-red absolute -top-1 -right-1"></div>
                <div className="rounded-full w-4 h-4 animate-ping bg-red absolute -top-1 -right-1"></div>
              </>
            )}
            <Button onClick={bot.onSave} className="w-32 ml-8">
              Salvar
            </Button>
          </div>
        </div>
      </ContentWrapper>
      <ConfirmModal
        shown={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={onConfirm}
      />
    </SidebarLayout>
  );
}

export default WhatsappBotPage;
